import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { StepIndicator } from 'frontend-utils';
import {
  ROUTE_NAME_FOR_STAGE_EDITORS,
  STAGES,
} from 'lumen/utils/constants/otb-selfservice-constants';

const DATA_BAR_STEPS = {
  INTRO: [STAGES.INTRO_PAGE],
  PMS_SELECTION: [STAGES.PMS_SELECTION_PAGE],
  PMS_ELIGIBILITY_QUESTIONS: [STAGES.PMS_ELIGIBILITY_QUESTIONS_PAGE],
  OTHER_SYSTEM_SELECTION: [
    STAGES.OTHER_SYSTEM_SELECTION_PAGE,
    STAGES.OTHER_SYSTEM_SELECTION_PREFILLED_PAGE,
  ],
  OTHER_SYSTEM_ELIGIBILITY_QUESTIONS: [
    STAGES.OTHER_SYSTEM_ELIGIBILITY_QUESTIONS_PAGE,
  ],
  SETUP: [STAGES.PMS_SETUP_PAGE],
  BENCHMARK: [STAGES.BENCHMARK_QUESTION_PAGE],
  DATA_VALIDATION: [STAGES.VALIDATING_DATA_PAGE],
} as const;

export type DataBarStep = (typeof DATA_BAR_STEPS)[keyof typeof DATA_BAR_STEPS];

interface SettingsMyotbOtbSelfServiceDataBarSignature {
  Args: {};
}

export default class SettingsMyotbOtbSelfServiceDataBar extends Component<SettingsMyotbOtbSelfServiceDataBarSignature> {
  @service declare access: Services['access'];
  @service declare myotbSs: Services['myotb-ss'];
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentStepIndex() {
    const currentStepStages = Object.values(DATA_BAR_STEPS).find((stepStages) =>
      stepStages.find((stage) =>
        // @ts-expect-error: Object is possibly 'null'.
        this.router.currentRouteName.includes(
          ROUTE_NAME_FOR_STAGE_EDITORS[stage] as string,
        ),
      ),
    );

    if (!currentStepStages) {
      return 0;
    }

    // The data bar value isn't zero-based, so add 1.
    return this.steps.indexOf(currentStepStages) + 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get steps() {
    const { otbSelfService } = this.myotbSs;
    const stage = otbSelfService?.stage;

    const steps: DataBarStep[] = [
      DATA_BAR_STEPS.INTRO,
      DATA_BAR_STEPS.PMS_SELECTION,
    ];

    // eligibilityQuestions will be an empty array if there are no questions, so check the length.
    if (otbSelfService?.pmsDatasource?.eligibilityQuestions?.length) {
      steps.push(DATA_BAR_STEPS.PMS_ELIGIBILITY_QUESTIONS);
    }

    if (
      otbSelfService?.otherSystem ||
      otbSelfService?.customOtherSystemName ||
      stage === STAGES.OTHER_SYSTEM_SELECTION_PAGE
    ) {
      steps.push(DATA_BAR_STEPS.OTHER_SYSTEM_SELECTION);
    }

    if (otbSelfService?.otherSystem?.eligibilityQuestions?.length) {
      steps.push(DATA_BAR_STEPS.OTHER_SYSTEM_ELIGIBILITY_QUESTIONS);
    }

    steps.push(DATA_BAR_STEPS.SETUP);

    if (this.access.hasFeature('otb.benchmark_question.view')) {
      steps.push(DATA_BAR_STEPS.BENCHMARK);
    }

    steps.push(DATA_BAR_STEPS.DATA_VALIDATION);

    return steps;
  }

  <template>
    <StepIndicator
      @currentStep={{this.currentStepIndex}}
      @isContinuous={{true}}
      @steps={{this.steps.length}}
    />
  </template>
}
